import axios, { AxiosError, AxiosResponse } from "axios";
import { authStore } from "../stores/AuthStore";
import { getActiveDepartmentCode, getCurrentHostName } from "../utils/ActiveDepartment";

export const host = process.env.REACT_APP_API_URL;
export type RunAllResponse = Record<string, string>;

// Create configured axios instance
const apiClient = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
});

// Add auth token to every request
apiClient.interceptors.request.use(async (config) => {
	const token = await authStore.getAccessToken();
	if (!token) {
		// No token available; reject the request to avoid 401 errors
		return Promise.reject(new Error('Not authenticated. Please log in.'));
	}
	const departmentCode = getActiveDepartmentCode();

	config.headers.authorization = token;
	if (departmentCode) {
		config.headers['X-QuikFin-Department-Code'] = departmentCode;
	}
	config.headers['X-QuikFin-Host'] = getCurrentHostName();
	return config;
});

// Centralized response handling
apiClient.interceptors.response.use(
	(response: AxiosResponse) => response, // Keep full response, extract data in functions
	(error: AxiosError) => Promise.reject(processError(error))
);

export const postRequest = async <T>(url: string, data?: unknown): Promise<T> => {
	const response = await apiClient.post<T>(url, data);
	return response.data;
};

export const getRequest = async <T>(url: string): Promise<T> => {
	const response = await apiClient.get<T>(url);
	return response.data;
};

/** use for public APIs that do not require authentication */
export const getNoAuth = async <T>(url: string): Promise<T> => {
	try {
		const response = await axios.get(url, { headers: { 'Content-Type': 'application/json' } });
		return response.data;
	} catch (error) {
		throw processError(error as AxiosError);
	}
};

export const putRequest = async <T>(url: string, data: unknown): Promise<T> => {
	const response = await apiClient.put<T>(url, data);
	return response.data;
};

export const patchRequest = async <T>(url: string, data?: unknown): Promise<T> => {
	const response = await apiClient.patch<T>(url, data);
	return response.data;
};

export const deleteRequest = async (url: string): Promise<void> => {
	await apiClient.delete(url);
};

const processError = (error: AxiosError): Error => {
	// Network error (no response received)
	if (error.code === 'ERR_NETWORK' || !error.response) {
		return new Error(`${error.message}. Please check your network connection and try again.`);
	}

	const status = error.response.status;
	const data = error.response.data;

	// Handle specific status codes
	if (status === 401) {
		return new Error('Unauthorized. Please log in and try again.');
	}
	if (status === 403) {
		return new Error(typeof data === 'string'
			? data
			: 'Not authorized to perform this action. If this is an error, please contact your administrator.');
	}
	if (status === 404) {
		return new Error('Unable to get the requested data.');
	}

	// Handle 400 with structured validation errors
	if (status === 400 && typeof data === 'object' && data && 'errors' in data) {
		const errors = (data as any).errors;
		// Convert validation errors object to readable string
		const errorMessages = Object.entries(errors)
			.map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
			.join('; ');
		return new Error(errorMessages || 'Validation failed');
	}

	// Handle any other error with data (like your BadRequest strings)
	if (data) {
		return new Error(typeof data === 'string' ? data : JSON.stringify(data));
	}

	// Fallback for unknown errors
	console.error('Unknown Error:', error);
	return new Error('An unknown error occurred. Please check your console/network for more details.');
}
